import { useEffect, useRef } from 'react'
import { useMapSectionData, SectionType } from '../contexts'

/**
 * Helper hook for tabs to easily set their map section data
 * Automatically sets the active section and updates data when mounted
 * Clears section data on unmount
 */
export const useEstimateMapSection = (
  sectionType: SectionType,
  sectionData?: Record<string, any>
) => {
  const {
    setActiveSection,
    updateSectionData,
    clearSectionData,
    setHoveredItemId,
    setSelectedItemId,
    setFlyToCoordinates,
    setOnItemClick,
  } = useMapSectionData()

  const previousSectionRef = useRef<SectionType>(null)

  useEffect(() => {
    // Only update if section changed
    if (previousSectionRef.current !== sectionType) {
      setActiveSection(sectionType)
      previousSectionRef.current = sectionType
    }

    if (sectionData) {
      updateSectionData(sectionData)
    }

    return () => {
      // Clear section data on unmount
      clearSectionData()
      setActiveSection(null)
    }
  }, [
    sectionType,
    sectionData,
    setActiveSection,
    updateSectionData,
    clearSectionData,
  ])

  return {
    setHoveredItemId,
    setSelectedItemId,
    setFlyToCoordinates,
    setOnItemClick,
    updateSectionData,
  }
}

/**
 * Hook for tabs without map visualization
 * Clears any existing map section data
 */
export const useClearMapSection = () => {
  const { setActiveSection, clearSectionData } = useMapSectionData()

  useEffect(() => {
    setActiveSection(null)
    clearSectionData()
  }, [setActiveSection, clearSectionData])
}
